export interface BlogPost {
  id: string;
  url: string;
  title: string;
  content: string;
  createdAt: string;
}

export interface AudioJob {
  id: string;
  postId: string;
  status: AudioJobStatus;
  audioUrl?: string;
  error?: string;
  createdAt: string;
  updatedAt: string;
}

export type AudioJobStatus = "pending" | "processing" | "completed" | "failed";

export interface ApiResponse<T = unknown> {
  success: boolean;
  data?: T;
  error?: string;
  code?: string;
}

export type VoiceGender = "male" | "female";

export interface GenerateRequest {
  siteId: string;
  articleId: string;
  content: string;
  voice?: VoiceGender;
}

export interface GenerateResponse {
  audioUrl: string;
  cached: boolean;
}

// Subscription & Usage Types
export type PlanType = "free" | "starter" | "growth" | "business";

export type SubscriptionStatus =
  | "active"
  | "past_due"
  | "canceled"
  | "incomplete"
  | "trialing";

/**
 * Appearance is deliberately absent from this list. A site owner controls their own
 * page's markup and stylesheet, so they can restyle `.ba-widget` whether or not we
 * let them — a customization gate would only be enforceable against the people
 * honest enough to use the dashboard. The paid tiers are defined by the three limits
 * below, all of which are enforced server-side or baked into the audio itself.
 */
export interface PlanLimits {
  /** null means uncapped. Every plan is capped today; the type allows for one that isn't. */
  maxArticlesPerMonth: number | null;
  allowedVoices: VoiceGender[];
  hasWatermark: boolean;
}

export const PLAN_LIMITS: Record<PlanType, PlanLimits> = {
  free: {
    maxArticlesPerMonth: 10,
    allowedVoices: ["male"],
    hasWatermark: true,
  },
  starter: {
    maxArticlesPerMonth: 30,
    allowedVoices: ["male", "female"],
    hasWatermark: false,
  },
  growth: {
    maxArticlesPerMonth: 75,
    allowedVoices: ["male", "female"],
    hasWatermark: false,
  },
  business: {
    maxArticlesPerMonth: 250,
    allowedVoices: ["male", "female"],
    hasWatermark: false,
  },
};

/**
 * The plan keys accepted at checkout. Single source of truth: the signup form, the
 * register endpoint's validation, and the checkout price map all agree on these.
 *
 * A key may carry an offer suffix, as "growth_annual_founding" does. The suffix buys
 * the SAME price as the unsuffixed key and only adds a coupon on the way into
 * Checkout, which is what lets an offer survive the gap between signup and payment:
 * the chosen key is parked in users.pending_plan during email verification, so an
 * offer encoded in the key needs no extra column to persist. It also means a browser
 * never names a coupon — it names a plan, and the server maps that to a coupon id it
 * holds itself. See couponForCheckoutPlanKey in apps/api/src/services/stripe.ts.
 */
export const CHECKOUT_PLAN_KEYS = [
  "starter_monthly",
  "starter_annual",
  "growth_monthly",
  "growth_annual",
  "business_monthly",
  "business_annual",
  "growth_annual_founding",
] as const;

export type CheckoutPlanKey = (typeof CHECKOUT_PLAN_KEYS)[number];

/**
 * Keys that exist only to open a NEW discounted subscription.
 *
 * They must never reach /auth/change-plan: repricing an existing subscription to the
 * same price is a no-op that still shows the customer a confirmation screen, and the
 * coupon would not apply to it anyway.
 */
export const OFFER_PLAN_KEYS: readonly CheckoutPlanKey[] = [
  "growth_annual_founding",
];

export function isOfferPlanKey(value: unknown): value is CheckoutPlanKey {
  return (
    typeof value === "string" &&
    (OFFER_PLAN_KEYS as readonly string[]).includes(value)
  );
}

/**
 * The plan a customer ends up on after an offer key is purchased.
 *
 * Stripe only knows the price, so a founding purchase reads back as "growth_annual"
 * from checkoutPlanKeyForPriceId. Comparing a stored subscription against the key the
 * customer originally clicked has to go through this, or a founding customer who
 * opens their link a second time looks like they are on a different plan.
 */
export function baseCheckoutPlanKey(
  value: string | null | undefined,
): string | null {
  if (!value) return null;
  return value === "growth_annual_founding" ? "growth_annual" : value;
}

export function isCheckoutPlanKey(value: unknown): value is CheckoutPlanKey {
  return (
    typeof value === "string" &&
    (CHECKOUT_PLAN_KEYS as readonly string[]).includes(value)
  );
}

export interface UserContext {
  userId: string;
  email: string;
  /** The plan the customer is paying for. Never downgraded by dunning. */
  planType: PlanType;
  /**
   * The plan whose *quota* currently applies. Equals planType unless the account is
   * past due beyond its grace window, in which case free-tier page limits apply.
   * Feature entitlements (voices, watermark) always follow planType — see
   * effectivePlanType() for why.
   */
  effectivePlanType: PlanType;
  siteId?: string;
  lifetimeGenerations?: number;
  isAdmin?: boolean;
}

/**
 * Which plan's quota applies right now.
 *
 * A past_due subscription keeps full access for `graceDays`, then falls back to
 * free-tier limits until payment succeeds. This is applied to the page quota only:
 * hasWatermark is part of the generated audio's content hash, so flipping it would
 * make every cached page miss and regenerate — spending *more* OpenAI money on a
 * customer who has stopped paying. Callers must read features from planType.
 */
export function effectivePlanType(
  planType: PlanType,
  pastDueSince: Date | null | undefined,
  graceDays: number,
  now: Date = new Date(),
): PlanType {
  if (!pastDueSince) return planType;

  const graceMs = graceDays * 24 * 60 * 60 * 1000;
  const graceExpired = now.getTime() - pastDueSince.getTime() >= graceMs;

  return graceExpired ? "free" : planType;
}

export interface UsageStats {
  monthYear: string;
  charactersGenerated: number;
  articlesGenerated: number;
  lifetimeGenerations: number;
  planType: PlanType;
  /**
   * The plan's page allowance, resolved server-side. Sent rather than looked up
   * in the dashboard because the web app does not depend on this package, and a
   * second copy of the limits would be free to drift from PLAN_LIMITS.
   */
  maxArticlesPerMonth: number | null;
}

export interface TopArticle {
  articleId: string;
  plays: number;
  listenSeconds: number;
  completions: number;
}

/**
 * What the subscription delivered this month, as opposed to what it consumed.
 * `articlesLive` is cumulative and never resets — it is the library the customer
 * keeps, and the counterweight to a quota gauge that only counts down.
 */
export interface ListenStats {
  monthYear: string;
  listenSeconds: number;
  plays: number;
  completions: number;
  uniqueSessions: number;
  articlesLive: number;
  topArticles: TopArticle[];
}

export interface VerifiedSite {
  id: string;
  siteId: string;
  domain: string;
  verifiedAt: string | null;
}

export interface UserProfile {
  id: string;
  email: string;
  planType: PlanType;
  lifetimeGenerations: number;
  stripeCustomerId: string | null;
  createdAt: string;
}

export interface SubscriptionInfo {
  id: string;
  status: SubscriptionStatus;
  priceId: string | null;
  currentPeriodStart: string | null;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
}

/** Which player the widget mounts when the host page gives no placement hint. */
export type DisplayMode = "popup" | "inline";

/** Where the inline player is inserted relative to its target element. */
export type InlineAnchor = "before" | "after";

export const DISPLAY_MODES: readonly DisplayMode[] = ["popup", "inline"];
export const INLINE_ANCHORS: readonly InlineAnchor[] = ["before", "after"];

/**
 * The premade player designs. All three share one DOM and differ only in CSS, so
 * switching between them never rebuilds the controls.
 */
export type PlayerDesign = "waveform" | "compact" | "card";

/**
 * "auto" draws the player from the host page's own text and background colours, so
 * it blends into light, dark and tinted pages without being told which one it is on.
 */
export type PlayerTheme = "auto" | "light" | "dark";

/** "inherit" uses the host page's font; "system" the platform UI font. */
export type PlayerFont = "inherit" | "system";

export const PLAYER_DESIGNS: readonly PlayerDesign[] = [
  "waveform",
  "compact",
  "card",
];
export const PLAYER_THEMES: readonly PlayerTheme[] = ["auto", "light", "dark"];
export const PLAYER_FONTS: readonly PlayerFont[] = ["inherit", "system"];

/** What an absent field means. One copy, read by the widget and the editors. */
export const DEFAULT_PLAYER_DESIGN: PlayerDesign = "waveform";
export const DEFAULT_PLAYER_THEME: PlayerTheme = "auto";
export const DEFAULT_DISCLAIMER_TEXT = "Audio narration is AI-generated.";

/**
 * The colour fields of `WidgetCustomization`. Every one is a #rgb / #rrggbb hex,
 * optional, and cleared by sending "" — so the sanitizer and the widget can treat
 * them as one group instead of seven copies of the same rule.
 */
export const CUSTOMIZATION_COLOR_KEYS = [
  "accentColor",
  "titleColor",
  "timeColor",
  "trackColor",
  "waveIdleColor",
  "hoverColor",
  "disclaimerColor",
] as const;

export type CustomizationColorKey = (typeof CUSTOMIZATION_COLOR_KEYS)[number];

/**
 * Per-site widget appearance. One record, two editors: the dashboard writes it through
 * the session-authenticated route, the WordPress plugin through the API-key one.
 *
 * Every field is optional and means "no opinion" when absent — that is what lets the
 * plugin PUT a partial update without clobbering something set in the dashboard.
 */
export interface WidgetCustomization {
  /** Heading above the player. Absent falls back to the mode-specific default. */
  panelTitle?: string;
  /**
   * Explicit false hides the heading entirely. Needed as its own field because the
   * sanitizer drops empty strings, so `panelTitle: ""` cannot round-trip.
   */
  showTitle?: boolean;
  /**
   * #rgb / #rrggbb. The play button and the played part of the waveform. Absent means
   * "adaptive ink": the host page's own text colour.
   */
  accentColor?: string;
  /** Only consulted when the page has no `#narratenow-player` placeholder. */
  displayMode?: DisplayMode;
  /** CSS selector the inline player anchors to, for sites that cannot add a div. */
  inlineTarget?: string;
  inlineAnchor?: InlineAnchor;
  /** One blob for both modes — `.ba-widget--inline` already scopes mode-specific rules. */
  customCss?: string;

  /** Absent means DEFAULT_PLAYER_DESIGN. */
  design?: PlayerDesign;
  /** Absent means DEFAULT_PLAYER_THEME. */
  theme?: PlayerTheme;
  /** Absent means "inherit". */
  fontFamily?: PlayerFont;

  // The colours below are all optional overrides of what the theme derives. Absent
  // means "let the theme decide", which is what keeps a premade design looking right
  // on any page.

  /** Behind the player. "transparent" lets the page show through ("match my site"). */
  backgroundColor?: string;
  /** The label above or inside the player. */
  titleColor?: string;
  /** Timestamps, and the skip / volume / settings icons. */
  timeColor?: string;
  /** The not-yet-played part of the waveform or progress bar. */
  trackColor?: string;
  /** The whole waveform before the first play. Absent follows the primary colour. */
  waveIdleColor?: string;
  /** The play button while hovered. Absent is derived from accentColor. */
  hoverColor?: string;

  /** Show a line of text under the player. */
  showDisclaimer?: boolean;
  /** The disclaimer line. Absent uses DEFAULT_DISCLAIMER_TEXT. HTML is stripped. */
  disclaimerText?: string;
  disclaimerColor?: string;
}

export interface SiteConfigResponse {
  plan: PlanType;
  allowedVoices: VoiceGender[];
  hasWatermark: boolean;
  widgetCustomization?: WidgetCustomization;
}

// Error codes for API responses
export const ErrorCodes = {
  MISSING_FIELDS: "MISSING_FIELDS",
  CONTENT_TOO_LONG: "CONTENT_TOO_LONG",
  INVALID_API_KEY: "INVALID_API_KEY",
  INVALID_SITE: "INVALID_SITE",
  SITE_NOT_OWNED: "SITE_NOT_OWNED",
  SITE_NOT_VERIFIED: "SITE_NOT_VERIFIED",
  ORIGIN_NOT_ALLOWED: "ORIGIN_NOT_ALLOWED",
  MONTHLY_LIMIT_REACHED: "MONTHLY_LIMIT_REACHED",
  VOICE_NOT_ALLOWED: "VOICE_NOT_ALLOWED",
  RATE_LIMIT_EXCEEDED: "RATE_LIMIT_EXCEEDED",
  ACCOUNT_SUSPENDED: "ACCOUNT_SUSPENDED",
  PAYMENT_PAST_DUE: "PAYMENT_PAST_DUE",
  INTERNAL_ERROR: "INTERNAL_ERROR",
} as const;

export type ErrorCode = (typeof ErrorCodes)[keyof typeof ErrorCodes];
